import type { SetupAreaDto, SetupProgressDto, SetupStatus } from '@bk/contracts';
import { navigate } from '../../lib/router';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Button } from '../../ui';

/** Percorso di configurazione: si aggiorna da solo mentre il sistema lavora. */
export function useSetupProgress(enabled = true) {
  return useTenantQuery<SetupProgressDto>(['onboarding-progress'], '/onboarding/progress', {
    enabled,
    refetchInterval: (d) =>
      // Anche mentre il cervello rifà un passo già pronto: le sue domande tornano appena ha finito.
      d?.passi.some((s) => s.stato === 'in_corso') ||
      // e mentre una fase lavora (il suo contatore è aperto): la colonna delle fasi resta vera.
      d?.wizard.some((s) => s.preparando || s.lavori.length || (!!s.tempi && !s.tempi.fine))
        ? 3000
        : 10_000,
  });
}

/** Una fase di un'area: icona e parola, leggibile anche senza colori. */
export function PhaseChip({
  label,
  stato,
  extra,
}: {
  label: string;
  stato: SetupStatus;
  extra?: string;
}) {
  const tone = {
    fatto: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    parziale: 'border-amber-200 bg-amber-50 text-amber-800',
    in_corso: 'border-accent/30 bg-accent/5 text-accent',
    da_fare: 'border-black/10 bg-white text-ink/50',
  }[stato];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${tone}`}
    >
      <span aria-hidden>
        {stato === 'fatto' ? '✓' : stato === 'parziale' ? '◐' : stato === 'in_corso' ? '…' : '○'}
      </span>
      {label}
      {extra && <span className="opacity-70">· {extra}</span>}
    </span>
  );
}

/** Le due fasi di un'area, in ordine (ADR 0041). */
export function AreaPhases({ a }: { a: SetupAreaDto }) {
  return (
    <div className="flex flex-wrap gap-1">
      <PhaseChip
        label="1 Verifica"
        stato={a.verifica.stato}
        extra={a.verifica.da_verificare ? `${a.verifica.da_verificare} da fare` : undefined}
      />
      <PhaseChip label="2 Approfondimento" stato={a.approfondimento.stato} />
    </div>
  );
}

function ProgressBar({ value }: { value: number }) {
  return (
    <span className="block h-2 w-full rounded-full bg-black/5">
      <span
        className="block h-2 rounded-full bg-accent transition-all"
        style={{ width: `${value}%` }}
      />
    </span>
  );
}

/**
 * Fascia compatta sopra le chat: a che punto è la configurazione e cosa fare adesso, con il rimando al percorso
 * completo.
 */
export function SetupBanner({ here }: { here?: string }) {
  const { tenant, isManager } = useTenant();
  const progress = useSetupProgress(isManager);
  const p = progress.data;
  if (!p) return null;
  const done = p.passi.filter((s) => s.stato === 'fatto').length;
  const go = (link: string) => navigate(`/t/${tenant.id}/${link}`);
  return (
    <div className="mb-3 rounded-xl border border-black/10 bg-white px-4 py-3 text-sm shadow-sm">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="flex min-w-[10rem] flex-1 items-center gap-2">
          <span className="w-28 shrink-0">
            <ProgressBar value={p.percentuale} />
          </span>
          <span className="text-xs text-ink/60">
            Configurazione {p.percentuale}% · {done} di {p.passi.length} passi
          </span>
        </span>
        <button
          className="text-xs font-medium text-accent hover:underline"
          onClick={() => go('configurazione')}
        >
          Vedi il percorso completo
        </button>
      </div>
      {p.prossimo && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-ink/45">Adesso</span>
          <span className="min-w-0 flex-1">{p.prossimo.testo}</span>
          {p.prossimo.link !== here && (
            <Button
              variant="ghost"
              className="!px-2 !py-1 text-accent"
              onClick={() => go(p.prossimo!.link)}
            >
              Vai
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
